/**
 * Token Manager
 *
 * Web:
 * - access token lives in memory only
 * - refresh token lives in the HttpOnly cookie
 *
 * Native:
 * - access token + refresh token are persisted in Capacitor Preferences
 * - refresh happens by posting the refresh token in the request body
 */

import { isNative, setNativeItem, getNativeItem, removeNativeItem } from '@/utils/secureStorage';

interface MemoryTokens {
  access_token: string;
  expires_at: number;
}

interface StoredTokens extends MemoryTokens {
  refresh_token: string;
  refresh_expires_at: number;
}

export interface SetTokensOptions {
  persistNative?: boolean;
}

type RefreshFailureKind = 'auth' | 'network' | 'server' | 'no_session';

const NATIVE_STORAGE_KEY = 'booka_auth_tokens';
const REFRESH_BUFFER_MS = 60 * 1000;
const DEFAULT_ACCESS_EXPIRES_IN = 300;
const NATIVE_REFRESH_EXPIRE_DAYS = 60;

class TokenManager {
  private static instance: TokenManager;

  private memory: MemoryTokens | null = null;
  private nativeRefreshToken: string | null = null;
  private nativeRefreshExpiresAt: number | null = null;
  private nativeSessionPersisted = false;

  private refreshPromise: Promise<string> | null = null;
  private initialized = false;
  private initializePromise: Promise<void> | null = null;
  private lastRefreshFailureKind: RefreshFailureKind | null = null;

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializePromise) {
      await this.initializePromise;
      return;
    }

    this.initializePromise = (async () => {
      if (!isNative()) {
        this.initialized = true;
        return;
      }

      try {
        const raw = await getNativeItem(NATIVE_STORAGE_KEY);
        console.log('[TokenManager] Native storage read complete.', {
          hasStoredValue: !!raw,
        });

        if (!raw) return;

        const stored: StoredTokens = JSON.parse(raw);
        if (!stored.access_token || !stored.refresh_token) {
          console.warn('[TokenManager] Corrupted native storage, clearing it.');
          await removeNativeItem(NATIVE_STORAGE_KEY);
          return;
        }

        this.memory = {
          access_token: stored.access_token,
          expires_at: stored.expires_at,
        };
        this.nativeRefreshToken = stored.refresh_token;
        this.nativeRefreshExpiresAt = stored.refresh_expires_at;
        this.nativeSessionPersisted = true;

        console.log('[TokenManager] Native tokens loaded from Preferences.', {
          hasAccessToken: !!this.memory.access_token,
          hasRefreshToken: !!this.nativeRefreshToken,
          accessExpiresAt: this.memory.expires_at,
          refreshExpiresAt: this.nativeRefreshExpiresAt,
        });
      } catch (err) {
        console.error('[TokenManager] Failed to load native tokens:', err);
        await removeNativeItem(NATIVE_STORAGE_KEY).catch(() => {});
      } finally {
        this.initialized = true;
      }
    })();

    try {
      await this.initializePromise;
    } finally {
      this.initializePromise = null;
    }
  }

  private async persistNative(): Promise<void> {
    if (!isNative() || !this.memory || !this.nativeRefreshToken) return;

    const stored: StoredTokens = {
      access_token: this.memory.access_token,
      expires_at: this.memory.expires_at,
      refresh_token: this.nativeRefreshToken,
      refresh_expires_at:
        this.nativeRefreshExpiresAt ?? Date.now() + NATIVE_REFRESH_EXPIRE_DAYS * 86400_000,
    };

    await setNativeItem(NATIVE_STORAGE_KEY, JSON.stringify(stored));
  }

  async setTokens(
    access_token: string,
    refresh_token: string,
    expires_in: number = DEFAULT_ACCESS_EXPIRES_IN,
    options: SetTokensOptions = {},
  ): Promise<void> {
    await this.initialize();

    if (!access_token?.trim()) throw new Error('[TokenManager] access_token is empty');
    if (!refresh_token?.trim()) throw new Error('[TokenManager] refresh_token is empty');

    const now = Date.now();
    const persistNative = options.persistNative ?? true;
    this.lastRefreshFailureKind = null;
    this.memory = {
      access_token,
      expires_at: now + expires_in * 1000,
    };

    if (isNative()) {
      this.nativeRefreshToken = refresh_token;
      this.nativeRefreshExpiresAt = now + NATIVE_REFRESH_EXPIRE_DAYS * 86400_000;
      this.nativeSessionPersisted = persistNative;

      if (persistNative) {
        await this.persistNative();
        console.log('[TokenManager] Tokens saved to Preferences.', {
          hasAccessToken: !!access_token,
          hasRefreshToken: !!refresh_token,
          accessExpiresAt: this.memory.expires_at,
          refreshExpiresAt: this.nativeRefreshExpiresAt,
        });
      } else {
        console.log('[TokenManager] Tokens stored for current native session only.');
      }
      return;
    }

    console.log('[TokenManager] Access token stored in memory (web).');
  }

  async getAccessToken(): Promise<string | null> {
    await this.initialize();
    const native = isNative();

    if (native) {
      if (
        this.nativeRefreshExpiresAt !== null &&
        Date.now() >= this.nativeRefreshExpiresAt
      ) {
        console.warn('[TokenManager] Native refresh token expired, clearing session.');
        await this.clearTokens();
        return null;
      }
    }

    const now = Date.now();

    if (this.memory) {
      const timeLeft = this.memory.expires_at - now;
      if (timeLeft > REFRESH_BUFFER_MS) {
        return this.memory.access_token;
      }

      console.log('[TokenManager] Access token expiring soon, refreshing...');
    } else {
      console.log(
        native
          ? '[TokenManager] No access token in memory, attempting native refresh...'
          : '[TokenManager] No access token in memory, attempting refresh via cookie...'
      );
    }

    return this.refreshAccessToken();
  }

  /** Force a refresh after a server rejects the current access token. */
  async forceRefreshAccessToken(): Promise<string | null> {
    await this.initialize();
    return this.refreshAccessToken();
  }

  async clearTokens(): Promise<void> {
    this.memory = null;
    this.nativeRefreshToken = null;
    this.nativeRefreshExpiresAt = null;
    this.nativeSessionPersisted = false;
    this.refreshPromise = null;
    this.lastRefreshFailureKind = null;

    if (isNative()) {
      await removeNativeItem(NATIVE_STORAGE_KEY).catch(() => {});
    }

    console.log('[TokenManager] Tokens cleared.');
  }

  async isAuthenticated(): Promise<boolean> {
    await this.initialize();

    if (isNative()) {
      if (!this.nativeRefreshToken) return false;
      if (this.nativeRefreshExpiresAt !== null && Date.now() >= this.nativeRefreshExpiresAt) {
        await this.clearTokens();
        return false;
      }
      return true;
    }

    const token = await this.getAccessToken();
    return token !== null;
  }

  async hasStoredSession(): Promise<boolean> {
    await this.initialize();

    if (this.memory?.access_token) return true;
    if (isNative() && !!this.nativeRefreshToken) return true;

    return false;
  }

  hasPersistentNativeSession(): boolean {
    return isNative() && this.nativeSessionPersisted;
  }

  getLastRefreshFailureKind(): RefreshFailureKind | null {
    return this.lastRefreshFailureKind;
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) {
      console.log('[TokenManager] Joining in-flight refresh request...');
      try {
        return await this.refreshPromise;
      } catch {
        return null;
      }
    }

    this.refreshPromise = this.performRefresh();

    try {
      const token = await this.refreshPromise;
      this.lastRefreshFailureKind = null;
      return token;
    } catch (err) {
      console.error('[TokenManager] Refresh failed:', err);
      const failureKind = (err instanceof Error && 'cause' in err
        ? (err as Error & { cause?: RefreshFailureKind }).cause
        : null) as RefreshFailureKind | null;

      this.lastRefreshFailureKind = failureKind ?? 'server';

      if (this.lastRefreshFailureKind === 'auth' || this.lastRefreshFailureKind === 'no_session') {
        await this.clearTokens();
      } else {
        console.warn('[TokenManager] Preserving session for retry later.', {
          failureKind: this.lastRefreshFailureKind,
          hasStoredSession: await this.hasStoredSession(),
        });
      }
      return null;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<string> {
    const { SERVICE_URLS } = await import('@/services/api');
    const url = `${SERVICE_URLS.USER_SERVICE}/auth/refresh`;
    const native = isNative();

    if (native && !this.nativeRefreshToken) {
      const recoveredGuestToken = await this.recoverGuestSession();
      if (recoveredGuestToken) return recoveredGuestToken;

      console.warn('[TokenManager] Native refresh requested without a refresh token in memory.');
      const error = new Error('[TokenManager] No refresh token available (native)') as Error & {
        cause?: RefreshFailureKind;
      };
      error.cause = 'no_session';
      throw error;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: native ? JSON.stringify({ refresh_token: this.nativeRefreshToken }) : undefined,
        credentials: 'include',
      });
    } catch (err) {
      const error = new Error(
        err instanceof Error ? err.message : '[TokenManager] Refresh request failed'
      ) as Error & { cause?: RefreshFailureKind };
      error.cause = 'network';
      throw error;
    }

    if (!response.ok) {
      if (response.status === 401) {
        const recoveredGuestToken = await this.recoverGuestSession();
        if (recoveredGuestToken) return recoveredGuestToken;
      }

      const err = await response.json().catch(() => ({}));
      const error = new Error(err.detail ?? `HTTP ${response.status}`) as Error & {
        cause?: RefreshFailureKind;
      };
      error.cause = response.status === 401 || response.status === 403 ? 'auth' : 'server';
      throw error;
    }

    const data = await response.json();
    if (!data.access_token) {
      throw new Error('[TokenManager] No access_token in refresh response');
    }

    const now = Date.now();
    const expiresIn: number = data.expires_in ?? DEFAULT_ACCESS_EXPIRES_IN;
    this.memory = {
      access_token: data.access_token,
      expires_at: now + expiresIn * 1000,
    };

    if (native && data.refresh_token) {
      this.nativeRefreshToken = data.refresh_token;
      this.nativeRefreshExpiresAt = now + NATIVE_REFRESH_EXPIRE_DAYS * 86400_000;

      if (this.nativeSessionPersisted) {
        await this.persistNative();
      }
    }

    console.log('[TokenManager] Tokens refreshed successfully.', {
      hasAccessToken: !!this.memory.access_token,
      hasRefreshToken: !!this.nativeRefreshToken,
    });
    return data.access_token;
  }

  /**
   * Recover only an onboarding guest session when the guest token pair is
   * unavailable. Registered users must never be replaced by a new guest.
   */
  private async recoverGuestSession(): Promise<string | null> {
    if (typeof window === 'undefined') return null;

    const marker = sessionStorage.getItem('booka_guest_session') === 'active';
    let tokenBelongsToGuest = false;

    if (this.memory?.access_token) {
      try {
        const payloadPart = this.memory.access_token.split('.')[1];
        const payload = payloadPart
          ? JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payloadPart.length / 4) * 4, '=')))
          : null;
        tokenBelongsToGuest = payload?.is_guest === true;
      } catch {
        tokenBelongsToGuest = false;
      }
    }

    if (!marker && !tokenBelongsToGuest) return null;

    try {
      const { SERVICE_URLS } = await import('@/services/api');
      const response = await fetch(`${SERVICE_URLS.USER_SERVICE}/auth/create-guest`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });

      if (!response.ok) return null;
      const data = await response.json();
      if (!data.access_token || !data.refresh_token) return null;

      await this.setTokens(data.access_token, data.refresh_token, data.expires_in ?? DEFAULT_ACCESS_EXPIRES_IN, {
        persistNative: false,
      });
      sessionStorage.setItem('booka_guest_session', 'active');
      console.warn('[TokenManager] Guest session recovered with a new guest user.');
      return data.access_token;
    } catch (error) {
      console.warn('[TokenManager] Failed to recover guest session.', error);
      return null;
    }
  }
}

export default TokenManager;
